import { useEffect, useMemo, useState } from "react";
import { articles, categories } from "@/mocks/chigua";
import SectionHeading from "./SectionHeading";

interface FeaturedProps {
  selected?: string;
  onSelect?: (key: string) => void;
}

function Featured({ selected = "all", onSelect }: FeaturedProps) {
  const [filter, setFilter] = useState(selected);

  useEffect(() => setFilter(selected), [selected]);

  const apply = (key: string) => {
    setFilter(key);
    onSelect?.(key);
  };

  const list = useMemo(
    () => (filter === "all" ? articles : articles.filter((a) => a.categoryKey === filter)),
    [filter]
  );

  const tabs = [{ key: "all", name: "全部", count: articles.length }, ...categories.map((c) => ({ key: c.key, name: c.name, count: c.count }))];

  return (
    <section id="featured" className="relative py-16 md:py-24">
      <div className="container-x">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading
            eyebrow="深度追踪"
            title="把每一口瓜，吃得明明白白"
            desc="按栏目浏览我们的原创文章，图文数据与来龙去脉一次讲清。"
          />
          <div className="inline-flex flex-wrap gap-1 rounded-full border border-background-800 bg-background-900 p-1">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => apply(t.key)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  filter === t.key
                    ? "bg-primary-500 text-background-950"
                    : "text-foreground-300 hover:text-foreground-50"
                }`}
              >
                {t.name}
                <span className={filter === t.key ? "ml-1.5 opacity-70" : "ml-1.5 text-foreground-600"}>
                  {filter === t.key ? "" : ""}
                  {t.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {list.map((a) => (
            <article
              key={a.id}
              data-category={a.categoryKey}
              className="group flex flex-col overflow-hidden rounded-2xl border border-background-800 bg-background-900/70 transition-colors hover:border-primary-500/40"
            >
              <a href="#story" className="block overflow-hidden">
                <div className="relative aspect-[3/2] overflow-hidden">
                  <img
                    src={a.image}
                    alt={`${a.title}配图`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-4 top-4 inline-flex items-center rounded-full bg-background-950/70 px-3 py-1 text-xs font-medium text-accent-300 backdrop-blur-sm">
                    {a.category}
                  </span>
                  <span className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-background-950/70 px-2.5 py-1 text-xs text-foreground-300 backdrop-blur-sm">
                    <i className="ri-eye-line" aria-hidden="true"></i>
                    {a.views}
                  </span>
                </div>
              </a>
              <div className="flex flex-1 flex-col p-5 md:p-6">
                <div className="flex items-center gap-2 text-xs text-foreground-500">
                  <span className="rounded-md bg-secondary-500/10 px-2 py-0.5 text-secondary-300">{a.tag}</span>
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-time-line" aria-hidden="true"></i>
                    {a.date} · {a.minutes} 分钟
                  </span>
                </div>
                <h3 className="mt-3 font-serif text-lg font-bold leading-snug text-foreground-50 md:text-xl">
                  <a href="#story" className="transition-colors hover:text-primary-300">
                    {a.title}
                  </a>
                </h3>
                <p className="mt-3 line-clamp-3 flex-1 text-sm leading-relaxed text-foreground-500">{a.excerpt}</p>
                <a
                  href="#story"
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-accent-300 transition-colors hover:text-accent-200"
                >
                  阅读全文
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5" aria-hidden="true"></i>
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Featured;